import { api } from "..";

export type StepUpMethod = "password" | "totp" | "telegram";

export interface CurrentCredential {
  method: StepUpMethod;
  password?: string;
  code?: string;
}

export interface TwoFactorStatus {
  method: StepUpMethod;
  hasPassword: boolean;
  hasTotp: boolean;
  hasTelegram: boolean;
}

export interface TotpSetupResponse {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
  expiresInSeconds: number;
}

export interface TelegramLinkStartResponse {
  token: string;
  botUsername: string;
  deepLink: string;
  expiresInSeconds: number;
}

export interface TelegramLinkStatusResponse {
  linked: boolean;
  method: StepUpMethod;
}

export const getTwoFactorStatus = async (): Promise<TwoFactorStatus> => {
  const r = await api.client.get("/2fa/status");
  return r.data;
};

export const startTotpSetup = async (): Promise<TotpSetupResponse> => {
  const r = await api.client.post("/2fa/totp/setup");
  return r.data;
};

export const confirmTotpSetup = async (params: {
  code: string;
  current?: CurrentCredential;
}): Promise<{ success: boolean; elevated: boolean }> => {
  const r = await api.client.post("/2fa/totp/confirm", params);
  return r.data;
};

export const activateTotp = async (params: {
  code: string;
  current?: CurrentCredential;
}): Promise<{ success: boolean; elevated: boolean }> => {
  const r = await api.client.post("/2fa/totp/activate", params);
  return r.data;
};

export const startTelegramLink = async (params: {
  current?: CurrentCredential;
}): Promise<TelegramLinkStartResponse> => {
  const r = await api.client.post("/2fa/telegram/link/start", params);
  return r.data;
};

export const getTelegramLinkStatus =
  async (): Promise<TelegramLinkStatusResponse> => {
    const r = await api.client.get("/2fa/telegram/link/status");
    return r.data;
  };

export const sendTelegramCode = async (): Promise<{
  expiresInSeconds: number;
}> => {
  const r = await api.client.post("/2fa/telegram/send-code");
  return r.data;
};

export const verifyStepUp = async (
  body:
    | { method: "password"; password: string }
    | { method: "totp"; code: string }
    | { method: "telegram"; code: string }
): Promise<{ elevated: true; expiresAt: string }> => {
  const r = await api.client.post("/2fa/verify", body);
  return r.data;
};

export const resetUserTwoFactor = async (
  identifier: string | number
): Promise<{ success: boolean }> => {
  const r = await api.client.post(
    `/2fa/reset/${encodeURIComponent(String(identifier))}`
  );
  return r.data;
};

export const activatePassword = async (params: {
  current?: CurrentCredential;
}): Promise<{ success: boolean; elevated: boolean }> => {
  const r = await api.client.post("/2fa/password/activate", params);
  return r.data;
};

export const recoverWithPassword = async (params: {
  password: string;
}): Promise<{ success: boolean; elevated: boolean }> => {
  const r = await api.client.post("/2fa/recovery", params);
  return r.data;
};
